/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm,
  CRow,
  CCol,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CButton,
  CSpinner,
  CAlert,
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const ShipmentStatusInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    shipmentStatusName: '',
    shipmentStatusDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        shipmentStatusName: initialData.ShipmentStatusName || '',
        shipmentStatusDescription: initialData.ShipmentStatusDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            shipmentStatusName: '',
            shipmentStatusDescription: ''
          });
        }
        setShowSuccess(false);
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.shipmentStatusName.trim()) {
      newErrors.shipmentStatusName = t('shipmentStatusNameRequired');
    } else if (formData.shipmentStatusName.length < 3) {
      newErrors.shipmentStatusName = 'Must be at least 3 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = localStorage.getItem('sessionUserId') || userData.sessionUserId;

      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }

      const submitData = {
        SessionUserId: String(userId),
        ShipmentStatusName: formData.shipmentStatusName.trim(),
        ShipmentStatusDescription: formData.shipmentStatusDescription.trim()
      };

      if (isEdit && initialData?.ShipmentStatusId) {
        submitData.ShipmentStatusId = initialData.ShipmentStatusId;
      }

      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        shipmentStatusName: '',
        shipmentStatusDescription: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    return isEdit ? t('shipmentStatusUpdated') : t('shipmentStatusCreated');
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateShipmentStatus') : t('createShipmentStatus');
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse && submitResponse.Result && submitResponse.Result.ErrorCode !== 0 && submitResponse.Result.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="shipmentStatusName">
              {t('shipmentStatusName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="shipmentStatusName"
              name="shipmentStatusName"
              value={formData.shipmentStatusName}
              onChange={handleInputChange}
              invalid={!!errors.shipmentStatusName}
              disabled={isLoading}
              placeholder={t('enterShipmentStatusName')}
            />
            {errors.shipmentStatusName && (
              <div className="invalid-feedback d-block">
                {errors.shipmentStatusName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-4">
          <CCol md={12}>
            <CFormLabel htmlFor="shipmentStatusDescription">
              {t('shipmentStatusDescription')}
            </CFormLabel>
            <CFormTextarea
              id="shipmentStatusDescription"
              name="shipmentStatusDescription"
              rows={4}
              value={formData.shipmentStatusDescription}
              onChange={handleInputChange}
              invalid={!!errors.shipmentStatusDescription}
              disabled={isLoading}
              placeholder={t('enterShipmentStatusDescription')}
            />
            {errors.shipmentStatusDescription && (
              <div className="invalid-feedback d-block">
                {errors.shipmentStatusDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" /> {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>

            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

ShipmentStatusInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool
};

export default ShipmentStatusInputs;
